import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Layout from "../../components/Layout/Layout";
import OrdersHeader from "../../components/GiftOrders/OrdersHeader";
import OrdersEmptyState from "../../components/GiftOrders/OrdersEmptyState";
import OrdersTable from "../../components/GiftOrders/OrdersTable";
import LoadingState from "../../components/Common/LoadingState";
import { getMyOrders, getOrderStatusList } from "../../api/orderApi";
import { useAsyncAction } from "../../hooks/useAsyncAction";
import { showErrorToast } from "../../utils/toast";

const GiftOrdersList = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [statusColors, setStatusColors] = useState({});
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const loadAction = useAsyncAction();
    const { run: runLoadOrders } = loadAction;
    const isLoadingOrders = loadAction.isLoading;
    const apiError = loadAction.errorMessage;

    useEffect(() => {
        if (!apiError) return;
        showErrorToast(apiError);
    }, [apiError]);

    useEffect(() => {
        let mounted = true;

        const loadOrders = async () => {
            const [result, statusList] = await Promise.all([
                runLoadOrders(
                    () => getMyOrders(),
                    { errorFallback: t("GIFT_ORDERS_ERROR_LOAD"), silentErrorToast: true }
                ),
                getOrderStatusList().catch(() => []),
            ]);
            if (!mounted) return;
            setOrders(result?.orders || []);
            const colorMap = {};
            statusList.forEach((s) => {
                if (s.title && s.color) colorMap[s.title.toLowerCase()] = s.color;
            });
            setStatusColors(colorMap);
            setHasLoadedOnce(true);
        };

        loadOrders();
        return () => {
            mounted = false;
        };
    }, [runLoadOrders, t]);

    const handleSelectOrder = (order) => {
        const resolvedOrderId = String(order?.id || "").replace("#", "").trim();
        if (!resolvedOrderId) return;
        navigate(`/gift-orders/${resolvedOrderId}`);
    };

    return (
        <Layout activeLabel="Gift orders">
            <div>
                <OrdersHeader
                    title={t("GIFT_ORDERS_TITLE")}
                    subtitle={t("GIFT_ORDERS_SUBTITLE")}
                />

                {isLoadingOrders && !hasLoadedOnce ? (
                    <LoadingState message={t("GIFT_ORDERS_LOADING")} />
                ) : orders.length ? (
                    <div>
                        <OrdersTable orders={orders} onSelect={handleSelectOrder} statusColors={statusColors} />
                    </div>
                ) : !isLoadingOrders && !orders.length ? (
                    <OrdersEmptyState />
                ) : null}
            </div>
        </Layout>
    );
};

export default GiftOrdersList;
